'use client';

import React, { useState } from 'react';
import {
  Globe,
  Gift,
  Mail,
  ShoppingCart,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


const funnelSteps = [
  {
    id: 'step-captacion',
    step: 1,
    title: 'Captación',
    subtitle: 'Visita a la landing page',
    description:
      'El usuario llega a myfindermf.base44.app desde anuncios, redes sociales o búsqueda orgánica. El bot registra la visita y activa el seguimiento.',
    icon: Globe,
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
    visitors: 3847,
    convRate: null,
    status: 'active' as const,
  },
  {
    id: 'step-leadmagnet',
    step: 2,
    title: 'Lead Magnet',
    subtitle: 'Comparación de precios gratis',
    description:
      'Se ofrece comparación gratuita de precios en 6+ tiendas (ahorro promedio 22%). El usuario deja su email o WhatsApp para recibir los resultados.',
    icon: Gift,
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent/20',
    visitors: 1247,
    convRate: 32.4,
    status: 'active' as const,
  },
  {
    id: 'step-secuencia',
    step: 3,
    title: 'Secuencia Automatizada',
    subtitle: '3–5 mensajes de email/WhatsApp',
    description:
      'Los bots envían una secuencia de 3 a 5 mensajes personalizados presentando los dossiers de viaje a €0.50. Incluye testimonios y ejemplos de ahorro.',
    icon: Mail,
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
    visitors: 312,
    convRate: 25.0,
    status: 'active' as const,
  },
  {
    id: 'step-oferta',
    step: 4,
    title: 'Oferta Principal',
    subtitle: 'Dossier de viaje completo a €0.50',
    description:
      'CTA directo al dossier de viaje a €0.50 vía PayPal en myfindermf.base44.app. El agente Andi guía al usuario durante el proceso de compra.',
    icon: ShoppingCart,
    color: 'text-warning',
    bg: 'bg-warning/10',
    border: 'border-warning/20',
    visitors: 95,
    convRate: 30.4,
    status: 'active' as const,
  },
  {
    id: 'step-upsell',
    step: 5,
    title: 'Upsell',
    subtitle: 'Más dossiers — descuento por volumen',
    description:
      'A compradores se les ofrece un pack de 5 dossiers a €2.00 (ahorro de €0.50). El bot retargeting reactiva compradores para nuevos destinos.',
    icon: TrendingUp,
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent/20',
    visitors: 31,
    convRate: 32.6,
    status: 'active' as const,
  },
];

export default function FunnelVisualizer() {
  const [expandedStep, setExpandedStep] = useState<string | null>('step-oferta');
  const [activated, setActivated] = useState(true);

  const handleActivate = () => {
    setActivated(!activated);
    toast[activated ? 'warning' : 'success'](
      activated ? 'Embudo pausado' : '¡Embudo activado y corriendo!'
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            Embudo de Ventas Automatizado
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            5 pasos · Visitante → Comprador de dossier €0.50
          </p>
        </div>
        <button
          onClick={handleActivate}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 active:scale-95 ${
            activated
              ? 'bg-accent/15 text-accent border border-accent/30 hover:bg-accent/25' :'bg-primary text-primary-foreground hover:bg-primary/90'
          }`}
        >
          {activated ? (
            <>
              <CheckCircle size={14} />
              Embudo Activo
            </>
          ) : (
            'Activar Embudo'
          )}
        </button>
      </div>

      <div className="p-5 space-y-0">
        {funnelSteps.map((step, idx) => {
          const Icon = step.icon;
          const isExpanded = expandedStep === step.id;
          const isLast = idx === funnelSteps.length - 1;

          return (
            <div key={step.id}>
              <button
                onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                className={`w-full text-left p-4 rounded-xl border transition-all duration-200 hover:bg-secondary/30 ${
                  isExpanded
                    ? `${step.bg} ${step.border}`
                    : 'border-border bg-transparent'
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Step number */}
                  <div
                    className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 ${
                      isExpanded
                        ? `${step.bg} ${step.color}`
                        : 'bg-secondary text-muted-foreground'
                    }`}
                  >
                    {step.step}
                  </div>

                  {/* Icon */}
                  <span className={`p-1.5 rounded-lg shrink-0 ${isExpanded ? step.bg : 'bg-secondary'}`}>
                    <Icon size={13} className={isExpanded ? step.color : 'text-muted-foreground'} />
                  </span>

                  {/* Title */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground">{step.title}</p>
                    <p className="text-2xs text-muted-foreground">{step.subtitle}</p>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="text-xs font-bold font-tabular text-foreground">
                        {step.visitors.toLocaleString('es-ES')}
                      </p>
                      <p className="text-2xs text-muted-foreground">usuarios</p>
                    </div>
                    {step.convRate !== null && (
                      <div className="text-right">
                        <p className={`text-xs font-bold font-tabular ${step.color}`}>
                          {step.convRate}%
                        </p>
                        <p className="text-2xs text-muted-foreground">conv.</p>
                      </div>
                    )}
                    {isExpanded ? (
                      <ChevronUp size={14} className="text-muted-foreground" />
                    ) : (
                      <ChevronDown size={14} className="text-muted-foreground" />
                    )}
                  </div>
                </div>

                {/* Expanded description */}
                {isExpanded && (
                  <div className="mt-3 ml-10 pl-3 border-l-2 border-border">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                )}
              </button>

              {/* Connector */}
              {!isLast && (
                <div className="funnel-step-connector" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}